import {useCallback, useEffect, useRef, useState} from 'react';
import type {PendingChatAttachment} from '../../aiMessageGenerator/utils/types';
import {isImageFile} from '../../aiMessageGenerator/utils/attachmentHelpers';
import {exceedsAiAgentSizeCap, isAiAgentHeicImage, isAiAgentSupportedFile} from '../utils/fileToDataUrl';
import {convertHeicToJpeg} from '../utils/convertHeicFile';
import {usePreventDropNavigation} from './usePreventDropNavigation';
import {
    ACCEPTED_FILE_TYPES,
    getTransferSupportedFile,
    warnFileTooLarge,
    warnUnsupportedFile,
} from '../utils/transferFile';

export function useAiAgentAttachment(onHasAttachmentChange?: (hasAttachment: boolean) => void) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingAttachmentRef = useRef<PendingChatAttachment | null>(null);
    // Stamps each pick: a slow HEIC conversion must not resurrect an attachment that was
    // removed, replaced or unmounted while it ran.
    const pickSeqRef = useRef(0);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [pendingFileName, setPendingFileName] = useState<string | null>(null);
    const [convertingFileName, setConvertingFileName] = useState<string | null>(null);

    const forgetPendingAttachment = useCallback(() => {
        pickSeqRef.current += 1;
        pendingAttachmentRef.current = null;
        setPreviewUrl(null);
        setPendingFileName(null);
        setConvertingFileName(null);
        onHasAttachmentChange?.(false);
    }, [onHasAttachmentChange]);

    const clearPendingAttachment = useCallback(() => {
        if(pendingAttachmentRef.current?.previewUrl) {
            URL.revokeObjectURL(pendingAttachmentRef.current.previewUrl);
        }
        forgetPendingAttachment();
    }, [forgetPendingAttachment]);

    const onHasAttachmentChangeRef = useRef(onHasAttachmentChange);
    onHasAttachmentChangeRef.current = onHasAttachmentChange;

    // The blob URL outlives this component unless it is released on unmount, and the
    // parent still thinks an attachment is pending unless it is told otherwise.
    useEffect(() => () => {
        pickSeqRef.current += 1;
        if(pendingAttachmentRef.current?.previewUrl) {
            URL.revokeObjectURL(pendingAttachmentRef.current.previewUrl);
        }
        if(pendingAttachmentRef.current) {
            pendingAttachmentRef.current = null;
            onHasAttachmentChangeRef.current?.(false);
        }
    }, []);

    usePreventDropNavigation(fileInputRef);

    const setPendingFile = useCallback(async (file: File) => {
        clearPendingAttachment();
        const pickSeq = ++pickSeqRef.current;
        let resolvedFile = file;
        if(isAiAgentHeicImage(file)) {
            setConvertingFileName(file.name);
            const converted = await convertHeicToJpeg(file);
            if(pickSeq !== pickSeqRef.current) {
                return;
            }
            setConvertingFileName(null);
            if(!converted) {
                warnUnsupportedFile();
                return;
            }
            resolvedFile = converted;
        }
        // Caught at pick time, not at send — a JPEG converted from HEIC can outgrow the cap.
        if(exceedsAiAgentSizeCap(resolvedFile)) {
            warnFileTooLarge();
            return;
        }
        const isImage = isImageFile(resolvedFile);
        const nextPreviewUrl = isImage ? URL.createObjectURL(resolvedFile) : '';
        pendingAttachmentRef.current = {file: resolvedFile, previewUrl: nextPreviewUrl};
        setPreviewUrl(nextPreviewUrl || null);
        setPendingFileName(isImage ? null : resolvedFile.name);
        onHasAttachmentChange?.(true);
    }, [clearPendingAttachment, onHasAttachmentChange]);

    const handleAttachClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if(!file) {
            return;
        }
        // The picker's `accept` is only a hint — a user can still choose a Word file or a
        // video, and dropping it silently looks like the attach button is broken.
        if(!isAiAgentSupportedFile(file)) {
            warnUnsupportedFile();
            return;
        }
        void setPendingFile(file);
    }, [setPendingFile]);

    const handlePaste = useCallback((event: React.ClipboardEvent) => {
        const {file, rejected} = getTransferSupportedFile(event.clipboardData);
        if(rejected) {
            event.preventDefault();
            warnUnsupportedFile();
            return;
        }
        if(!file) {
            return;
        }
        event.preventDefault();
        void setPendingFile(file);
    }, [setPendingFile]);

    // Without preventDefault on dragover the browser never fires drop — it navigates to the file.
    const handleDragOver = useCallback((event: React.DragEvent) => {
        if(Array.from(event.dataTransfer?.items ?? []).some(item => item.kind === 'file')) {
            event.preventDefault();
        }
    }, []);

    const handleDrop = useCallback((event: React.DragEvent) => {
        const {file, rejected} = getTransferSupportedFile(event.dataTransfer);
        if(rejected) {
            event.preventDefault();
            warnUnsupportedFile();
            return;
        }
        if(!file) {
            return;
        }
        event.preventDefault();
        void setPendingFile(file);
    }, [setPendingFile]);

    // The caller takes ownership of the blob URL, so it must not be revoked here.
    const takePendingAttachment = useCallback(() => {
        const pendingAttachment = pendingAttachmentRef.current;
        forgetPendingAttachment();
        return pendingAttachment;
    }, [forgetPendingAttachment]);

    return {
        fileInputRef,
        previewUrl,
        pendingFileName,
        convertingFileName,
        isConverting: convertingFileName !== null,
        acceptedFileTypes: ACCEPTED_FILE_TYPES,
        handleAttachClick,
        handleFileChange,
        handlePaste,
        handleDragOver,
        handleDrop,
        handleRemoveAttachment: clearPendingAttachment,
        takePendingAttachment,
    };
}
